import { ComponentRef, useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { MainContent } from "@/layout/styles";

/** Where each page was scrolled to, by its path, for the length of the tab. */
const positions = new Map<string, number>();

/**
 * Scrolls the content column for a route change.
 *
 * A new page starts at the top. A page the player comes back to with the
 * browser's Back (or Forward) button starts where they left it: the column's
 * position is noted as they scroll and put back on return. It used to scroll
 * smoothly to the top on every change, Back included, so returning from a
 * game to a long lobby meant watching the old page glide up and then
 * finding the place again.
 */
export const useScrollToTopOnRouteChange = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const scrollRef = useRef<ComponentRef<typeof MainContent> | null>(null);
  const currentPath = useRef(pathname);
  currentPath.current = pathname;

  // Note the position as the player scrolls, against the page they are on.
  useEffect(() => {
    const scrollElement = scrollRef.current?.getScrollElement();
    if (!scrollElement) return;

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        positions.set(currentPath.current, scrollElement.scrollTop);
      });
    };
    scrollElement.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      scrollElement.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current?.getScrollElement();
    if (!scrollElement) return;

    const remembered = navigationType === "POP" ? positions.get(pathname) : undefined;
    if (remembered === undefined) {
      scrollElement.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    // The page's content arrives after it mounts, so the position is put back
    // over a few frames rather than once, until it sticks or the player moves.
    let attempts = 0;
    let frame = 0;
    const restore = () => {
      scrollElement.scrollTo({ top: remembered, behavior: "auto" });
      attempts += 1;
      if (Math.abs(scrollElement.scrollTop - remembered) > 1 && attempts < 30) {
        frame = requestAnimationFrame(restore);
      }
    };
    frame = requestAnimationFrame(restore);

    return () => cancelAnimationFrame(frame);
    // Path only: a query-only change (a list's filter in the URL) is not a new page.
  }, [pathname]);

  return scrollRef;
};
